import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { NextPlanOutlined } from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SummaryKpiCards from "./SummaryKpiCards";
import SummarySeverityChart from "./SummarySeverityChart";
import SummaryFrameworkComplianceChart from "./SummaryFrameworkComplianceChart";
import SummaryCostChart from "./SummaryCostChart";
import SummaryServicesTable from "./SummaryServicesTable";
import { summaryCardTheme as t } from "./styles";
import { useSummaryData } from "./useSummaryData";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

/**
 * Dashboard Actionable Insights — compact strip; header links to Summary page; click body to open charts + table dialog.
 */
function SummaryCard() {
  const history = useHistory();
  // Dashboard card always shows org-wide summary (no account filter)
  const { data, loading, error, refetch } = useSummaryData();
  const [open, setOpen] = useState(false);

  const overall = data?.overall ?? null;
  const services = data?.services ?? [];

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleGoToSummary = () => history.push("/summary");

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          fontFamily: t.fontFamily,
          backgroundColor: t.colors.card,
          border: `1px solid ${t.colors.border}`,
          borderRadius: "8px",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          padding: "8px 12px 6px 12px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Header — same style as other dashboard cards */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          sx={{
            mb: 0.75,
            paddingBottom: "2px",
            borderBottom: `1px solid ${t.colors.border}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "text.primary" }}>
            ARNs Insight
          </Typography>
          <Box display="flex" alignItems="center">
            <Tooltip title="Click to view charts & table">
              <IconButton
                size="small"
                aria-label="view-charts"
                onClick={(e) => { e.stopPropagation(); handleOpen(); }}
                sx={{ p: 0, color: t.colors.textMuted, "&:hover": { color: t.colors.text } }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, alignSelf: "center", height: "18px" }} />
            <Tooltip title="Open Summary page">
              <IconButton
                aria-label="go-to-summary"
                size="small"
                sx={{ p: 0, color: "text.secondary", transform: "rotate(-45deg)" }}
                onClick={handleGoToSummary}
              >
                <NextPlanOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, alignSelf: "center", height: "18px" }} />
            <IconButton aria-label="drag-handle" className="drag-handle" sx={{ color: "text.secondary", fontSize: "20px", p: 0 }}>
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Body — clickable to open dialog */}
        <Box
          onClick={!error ? handleOpen : undefined}
          sx={{
            flex: 1,
            minHeight: 0,
            cursor: error ? "default" : "pointer",
            transition: "opacity 0.2s ease",
            "&:hover": !error ? { opacity: 0.9 } : {},
          }}
        >
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, minHeight: 40 }}>
              <CircularProgress size={18} sx={{ color: t.colors.accent }} />
              <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.8125rem", fontFamily: t.fontFamily }}>
                Loading…
              </Typography>
            </Box>
          )}

          {error && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
              <Typography variant="body2" sx={{ color: t.colors.issue, fontSize: "0.8125rem", fontFamily: t.fontFamily }}>
                {error}
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); refetch(); }}
                sx={{ textTransform: "none", fontSize: "0.75rem", color: t.colors.accent, fontFamily: t.fontFamily, minWidth: 0 }}
              >
                Retry
              </Button>
            </Box>
          )}

          {!loading && !error && (
            <>
              <Box sx={{ mt: 0.5 }}>
                <SummaryKpiCards overall={overall} compact />
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: t.radius.lg,
            border: `1px solid ${t.colors.border}`,
            boxShadow:
              "0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 10px -5px rgba(0,0,0,0.04)",
            maxHeight: "90vh",
            // Responsive width: comfortable on large screens, constrained on small
            width: { xs: "100%", sm: "95vw", md: "90vw", lg: "80vw" },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 2,
            // Avoid horizontal scroll in the expanded view while allowing vertical scrolling
            overflowX: "hidden",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: t.colors.text, fontFamily: t.fontFamily }}>
              ARNs Insight — charts overview
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Close"
              sx={{
                color: t.colors.textMuted,
                "&:hover": { color: t.colors.text, backgroundColor: t.colors.borderLight },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ mb: 2 }}>
            <SummaryKpiCards overall={overall} />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              mb: 2,
            }}
          >
            <SummarySeverityChart
              securityFailedBySeverity={overall?.security_failed_checks_by_severity_total}
            />
            <SummaryFrameworkComplianceChart overall={overall} />
            <SummaryCostChart overall={overall} services={services} />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SummaryCard;
